import { useState } from "react";

const FORM_URL = "https://readdy.ai/api/form/d7f2m8p5vm7vckkodrq0";

export default function ContactSection() {
  const [status, setStatus] = useState<"idle" | "sending" | "success" | "error">("idle");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus("sending");
    const form = e.currentTarget;
    const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
    try {
      const res = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      if (res.ok) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  return (
    <section id="contact" className="py-28 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="inline-block text-xs font-semibold uppercase tracking-widest text-gray-400 mb-4">
            // Get In Touch
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900">Contact CERTROM SRL</h2>
          <p className="text-gray-500 text-lg mt-4 max-w-xl mx-auto">
            Have questions about our iOS applications or want to learn more about our work? We&apos;d love to hear from you.
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-12">
          {/* Contact Info */}
          <div className="lg:w-2/5 flex flex-col gap-6">
            {/* Address Card */}
            <div className="bg-[#F9F8F6] rounded-2xl p-7">
              <h3 className="text-sm font-bold uppercase tracking-widest text-gray-400 mb-6">Office Address</h3>
              <div className="flex flex-col gap-5">
                <div className="flex gap-4 items-start">
                  <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-gray-200 shrink-0">
                    <i className="ri-map-pin-line text-gray-600 text-base" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-1">Address</p>
                    <p className="text-gray-800 text-sm leading-relaxed">
                      CALEA 13 SEPTEMBRIE NR. 231A SC. 1 ET. 14 AP. 54
                    </p>
                  </div>
                </div>
                <div className="flex gap-4 items-start">
                  <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-gray-200 shrink-0">
                    <i className="ri-building-2-line text-gray-600 text-base" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-1">City</p>
                    <p className="text-gray-800 text-sm">Bucharest</p>
                  </div>
                </div>
                <div className="flex gap-4 items-start">
                  <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-gray-200 shrink-0">
                    <i className="ri-barcode-line text-gray-600 text-base" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-1">ZIP Code</p>
                    <p className="text-gray-800 text-sm">050722</p>
                  </div>
                </div>
                <div className="flex gap-4 items-start">
                  <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-gray-200 shrink-0">
                    <i className="ri-flag-line text-gray-600 text-base" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-1">Country</p>
                    <p className="text-gray-800 text-sm">Romania</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Methods */}
            <div className="bg-[#F9F8F6] rounded-2xl p-7 flex flex-col gap-5">
              <h3 className="text-sm font-bold uppercase tracking-widest text-gray-400 mb-1">Contact Methods</h3>
              <div className="flex gap-4 items-center">
                <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-gray-200 shrink-0">
                  <i className="ri-mail-line text-gray-600 text-base" />
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-1">Email</p>
                  <p className="text-gray-800 text-sm">info@certromsrl.com</p>
                </div>
              </div>
              <div className="flex gap-4 items-center">
                <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-gray-200 shrink-0">
                  <i className="ri-phone-line text-gray-600 text-base" />
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-1">Phone Number</p>
                  <p className="text-gray-800 text-sm">+1 7405950984</p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:w-3/5">
            <form
              id="contact-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="bg-[#F9F8F6] rounded-2xl p-8 flex flex-col gap-5"
            >
              <div className="flex flex-col sm:flex-row gap-5">
                <div className="flex-1 flex flex-col gap-1.5">
                  <label className="text-xs font-semibold uppercase tracking-widest text-gray-500">Full Name</label>
                  <input
                    type="text"
                    name="full_name"
                    required
                    placeholder="Your full name"
                    className="bg-white border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-800 placeholder-gray-400 outline-none focus:border-gray-400 transition-colors"
                  />
                </div>
                <div className="flex-1 flex flex-col gap-1.5">
                  <label className="text-xs font-semibold uppercase tracking-widest text-gray-500">Email Address</label>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="your@email.com"
                    className="bg-white border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-800 placeholder-gray-400 outline-none focus:border-gray-400 transition-colors"
                  />
                </div>
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold uppercase tracking-widest text-gray-500">Subject</label>
                <input
                  type="text"
                  name="subject"
                  required
                  placeholder="How can we help?"
                  className="bg-white border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-800 placeholder-gray-400 outline-none focus:border-gray-400 transition-colors"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold uppercase tracking-widest text-gray-500">Message</label>
                <textarea
                  name="message"
                  required
                  rows={5}
                  maxLength={500}
                  placeholder="Write your message here... (max 500 characters)"
                  className="bg-white border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-800 placeholder-gray-400 outline-none focus:border-gray-400 transition-colors resize-none"
                />
              </div>
              <button
                type="submit"
                disabled={status === "sending"}
                className="w-full bg-gray-900 text-white rounded-lg py-3.5 text-sm font-semibold tracking-wide hover:bg-gray-800 transition-colors cursor-pointer disabled:opacity-60 whitespace-nowrap"
              >
                {status === "sending" ? "Sending..." : "Send Message"}
              </button>
              {status === "success" && (
                <p className="text-emerald-600 text-sm text-center font-medium">
                  Message sent successfully! We&apos;ll be in touch soon.
                </p>
              )}
              {status === "error" && (
                <p className="text-red-500 text-sm text-center font-medium">
                  Something went wrong. Please try again or email us directly.
                </p>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
